
import { Component, Vue } from "vue-property-decorator";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vTable from "@/components/vTable.vue";
import vModal from "@/components/vModal.vue";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import ListRequest, { Ordering, Filter, OrderType } from "@/models/ListRequest";
import { getErrorMessage } from "@/utils/Helpers";
import { RecipeCost } from "@/models/RecipeCost/RecipeCost";
import { MeasurementCategory } from "@/models/Measurement/MeasurementCategory";
import IngredientCostRepository from "@/utils/API/IngredientCostRepository";
import { dateTimeDisplayFormat } from "@/utils/Defaults";
import MeasurementRepository from "../../utils/API/MeasurementRepository";
import { Recipe } from "@/models/Recipes/Recipe";
import RecipeCostRepository from "@/utils/API/RecipeCostRepository";
import vSelect2 from "@/components/vSelect2.vue";
import vAutocomplete from "@/components/vAutocomplete.vue";

const vSelectFx = () => import("@/components/vSelectFx.vue");

@Component({
  components: {
    vBreadcrumbs,
    vModal,
    vSelect2,
    vTable,
    vAutocomplete,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
class RecipeCostComponent extends Vue {
  localizationState: any;

  recipeCost: any = this.getEmptyRecipeCost();
  recipeCosts: RecipeCost[] = [];
  recipeIngredientCosts: any[] = [];
  subRecipeCosts: any[] = [];
  recipe: Recipe = new Recipe();

  ingredientCost: any = {};
  allIngredientCosts: any[] = [];
  newIngredientCost: any;
  massMeasurements: any[] = [];
  vendors: any[] = [];
  addingVendorName: string = "";
  loading = false;

  totalCount: number = 0;
  pageSize: number = 5;
  count: number = 0;
  page: number = 1;
  filter: Filter = {
    name: "",
    value: "",
  };
  ordering: Ordering = {
    field: "",
    orderType: OrderType.Asc,
  };

  deletingModal = {
    isOpen: false,
    id: 0,
  };

  addingIngredientCostModal = {
    isOpen: false,
  };

  addingVendorModal = {
    isOpen: false,
  };

  addingMeasurementModal = {
    ingredientCostId: 0,
    isOpen: false,
    newMeasurements: new Array(),
    ingrCostIndex: -1,
  };

  ingredientCostSelected(ingredientCost: any, recipeIngredientCost: any) {
    if (recipeIngredientCost.ingredientCostId) {
      ingredientCost = this.allIngredientCosts.find(
        (x) => x.id == recipeIngredientCost.ingredientCostId
      );
      if (ingredientCost) {
        recipeIngredientCost.ingredientCostName = ingredientCost.name;
        recipeIngredientCost.costPerGram = ingredientCost.costPerGram;
      }
      else {
        recipeIngredientCost.ingredientCostName = "";
        recipeIngredientCost.costPerGram = 0;
      }
      recipeIngredientCost.isIngredientCostDeleted = false;
      recipeIngredientCost.isIngredientCostUpdated =
        ingredientCost.costPerGram !== recipeIngredientCost.costPerGram;
    }
  }

  vendorForRecipeIngredientCostChanged(recIngrCost: any) {
    let ingredientCostsForVendor = this.getIngredientCostsForVendor(
      recIngrCost.ingredientCostVendorId
    );
    recIngrCost.ingredientCostId = null;
    recIngrCost.costPerGram = 0;
  }

  changePage(args: any) {
    let page = args[0];
    let filters = args[2];
    let orderings = args[3];

    this.page = page;
    this.ordering =
      orderings.length > 0
        ? orderings[0]
        : { field: "", orderType: OrderType.Asc };
    this.filter = filters.length > 0 ? filters[0] : { name: "", value: "" };

    this.getRecipeCostsForRecipe();
  }

  order(filters: Filter[], orders: Ordering) {
    this.ordering = orders;
    this.filter = filters.length > 0 ? filters[0] : { name: "", value: "" };

    this.getRecipeCostsForRecipe();
  }

  // Recalculate
  recalculateRecipeCost() {
    this.recipeIngredientCosts.map((x: any) => {
      let ingredientCost = this.allIngredientCosts.find(
        (y: any) => y.id === x.ingredientCostId
      );
      if (ingredientCost) {
        x.costPerGram = ingredientCost.costPerGram;
        x.isIngredientCostUpdated = false;
      }
    });
  }

  // Update calculate and save
  updateAndSaveRecipeCost() {
    this.$snotify.async(
      this.translations.get("ingr-cost-loading"),
      () =>
        new Promise((success, reject) => {
          let recipeIngrCosts = this.recipeIngredientCosts.map((x) => {
            return {
              id: x.id,
              ingredientCostId: x.ingredientCostId,
              recipeIngredientId: x.recipeIngredientId,
              ingredientCostName: x.ingredientCostName,
              ingredientCostVendorId: x.ingredientCostVendorId,
              costPerGram: x.costPerGram,
            };
          });
          RecipeCostRepository.updateRecipeCost({
            recipeCostId: this.recipeCost.id,
            recipeIngredientCosts: recipeIngrCosts,
          })
            .then(() => {
              success({
                body: this.translations.get("rec-cost-update-success"),
              });
            })
            // @ts-ignore
            .catch((error) => {
              reject({
                body: getErrorMessage(error, this.translations),
              });
            });
        }),
      { closeOnClick: true, timeout: 3500 }
    );
  }

  // Remove measurement row
  removeMeasurementRow(index: any) {
    this.addingMeasurementModal.newMeasurements.splice(index, 1);
    setTimeout(() => {
      this.$validator.validateAll("measurement");
    }, 300);
  }

  // Save ingredient cost button modal
  saveIngredientCost() {
    this.$validator.validateAll("ingredientCost").then((result) => {
      if (result) {
        // @ts-ignore
        this.$snotify.async(
          this.translations.get("ingr-cost-loading"),
          () =>
            new Promise((success, reject) => {
              IngredientCostRepository.createIngredientCosts([
                this.ingredientCost,
              ])
                .then(() => {
                  this.addingIngredientCostModal.isOpen = false;
                  this.loadAllIngredientCosts();
                  success({
                    body: this.translations.get("ms_save_success"),
                  });
                })
                .catch((error) => {
                  reject({
                    body: getErrorMessage(error, this.translations),
                  });
                });
            }),
          { closeOnClick: true, timeout: 3500 }
        );
      }
    });
  }

  // Rediret to Edit Recipe Cost Page
  goOnEditRecipeCost(recipeCostId: string) {
    this.$router.push({
      name: "updateRecipeCost",
      params: { id: recipeCostId },
    });
  }

  // On delete recipe cost button
  deleteRecipeCost(id: number) {
    let recCost = this.recipeCosts.find((x) => x.id === id);
    if (recCost != undefined) {
      this.deletingModal.isOpen = true;
      this.deletingModal.id = recCost.id;
    }
  }

  // Delete recipe cost
  submitDelete() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("ingr-cost-loading"),
      () =>
        new Promise((success, reject) => {
          RecipeCostRepository.deleteRecipeCost(this.deletingModal.id)
            .then(() => {
              this.deletingModal.isOpen = false;
              this.loadAllIngredientCosts();
              this.getRecipeCostsForRecipe();
              success({
                body: this.translations.get("ingr-cost-delete-success"),
              });
            })
            .catch((error) => {
              reject({ body: getErrorMessage(error, this.translations) });
            });
        }),
      {
        timeout: 3500,
        closeOnClick: true,
      }
    );
  }

  // Open Add ingredient cost modal
  openAddIngredientCostModal() {
    this.ingredientCost = this.getEmptyIngredientCost();
    this.addingIngredientCostModal.isOpen = true;
  }

  // Load all ingredient cost
  loadAllIngredientCosts() {
    let reqData = new ListRequest({
      page: 1,
      pageSize: 9999,
      filters: [],
      orders: [],
    });
    IngredientCostRepository.listIngredientCost(reqData).then(
      (res) => {
        this.allIngredientCosts = res.data.result;
      },
      () => {}
    );
  }

  // Open Add measurement modal
  openAddMeasurementModal() {
    this.addingMeasurementModal.newMeasurements.length = 0;
    this.addingMeasurementModal.isOpen = true;
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("ingr-cost-loading"),
      () =>
        new Promise((success, reject) => {
          IngredientCostRepository.loadMeasurementsForIngredientCost({
            id: null,
            name: this.ingredientCost.name,
            measurements: this.ingredientCost.ingredientCostMeasurements,
          })
            .then((response) => {
              if (response.data.result && response.data.result.length > 0) {
                this.addingMeasurementModal.newMeasurements.shift();
              }
              // @ts-ignore
              response.data.result.map((x) => {
                this.addingMeasurementModal.newMeasurements.push({
                  // @ts-ignore
                  name: x.name,
                  // @ts-ignore
                  weight: (Math.round(x.weightInGrams * 100) / 100).toFixed(2),
                });
              });
              success({
                body: this.translations.get("ms_save_success"),
              });
            })
            .catch((error) => {
              reject({
                body: getErrorMessage(error, this.translations),
              });
            });
        }),
      {
        timeout: 1000,
        closeOnClick: true,
      }
    );
    // @ts-ignore
    this.addingMeasurementModal.newMeasurements.push(this.getEmptyMeasurment());
  }

  // Open Add vendor modal
  openAddVendorModal() {
    this.addingVendorModal.isOpen = true;
  }

  // Add new vendor
  saveIngredientCostVendor() {
    this.$validator.validateAll("vendor").then((result) => {
      if (result) {
        // @ts-ignore
        this.$snotify.async(
          this.translations.get("ingr-cost-loading"),
          () =>
            new Promise((success, reject) => {
              IngredientCostRepository.createIngredientCostVendor(
                this.addingVendorName
              )
                .then(() => {
                  this.addingVendorModal.isOpen = false;
                  this.loadIngredientCostVendors();
                  this.addingVendorName = "";
                  success({
                    body: this.translations.get("ingr-cost-vendor-success"),
                  });
                })
                .catch((error) => {
                  reject({
                    body: getErrorMessage(error, this.translations),
                  });
                });
            }),
          {
            timeout: 3500,
            closeOnClick: true,
          }
        );
      }
    });
  }

  // Add new row in new ingredien costs modal
  addNewMeasurementRow() {
    // @ts-ignore
    this.addingMeasurementModal.newMeasurements.push(this.getEmptyMeasurment());
  }

  // Add new measurement
  addNewMeasurement() {
    this.$validator.validateAll("measurement").then((result) => {
      if (result) {
        // @ts-ignore
        let ingrCost = this.ingredientCost;
        //  @ts-ignore
        let measurementNames = ingrCost.ingredientCostMeasurements.map(
          //  @ts-ignore
          (x) => x.name
        );
        this.addingMeasurementModal.newMeasurements.map((x) => {
          //  @ts-ignore
          if (!measurementNames.includes(x.name)) {
            ingrCost.ingredientCostMeasurements.push({
              //  @ts-ignore
              name: x.name,
              //  @ts-ignore
              weightInGrams: x.weight,
            });
          }
          return x;
        });
        this.addingMeasurementModal.isOpen = false;
      }
    });
  }

  // Load list of ingredient cost Vendors
  loadIngredientCostVendors() {
    IngredientCostRepository.listIngredientCostVendors()
      .then((response) => {
        let vendors = response.data.result.map((x: any) => {
          return {
            id: x.id,
            name: x.name,
          };
        });
        this.vendors = vendors;
      })
      .catch((error) => {
        this.$snotify.error(getErrorMessage(error, this.translations));
      });
  }

  // Request for get recipe cost for recipe
  getRecipeCostsForRecipe() {
    if (this.recipeCost.recipeId) {
      let orderings = [this.ordering];

      let reqData = new ListRequest({
        page: this.page,
        pageSize: this.pageSize,
        filters: [{ name: "RecipeId", value: this.recipeCost.recipeId }],
        orders: orderings,
      });

      this.loading = true;
      RecipeCostRepository.listRecipeCosts(reqData)
        .then((response) => {
          this.count = response.data.count;
          this.totalCount = response.data.totalCount;
          this.recipeCosts = response.data.result;
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.$snotify.error(getErrorMessage(error, this.translations));
        });
    }
  }

  //Get recipe cost details
  getRecipeCostDetails() {
    RecipeCostRepository.getRecipeCostDetails(parseInt(this.$route.params.id))
      .then((response) => {
        this.recipeCost = response.data.result;
        this.getRecipeCostsForRecipe();
        this.recipeIngredientCosts = this.recipeCost.recipeIngredientCosts.map(
          (x: any) => {
            return {
              id: x.id,
              recipeIngredientId: x.recipeIngredientId,
              ingredientCostId: x.ingredientCostId,
              ingredientCostName:
                x.ingredientCostName !== null ? x.ingredientCostName : "",
              ingredientCostVendorId: x.ingredientCostVendorId,
              costPerGram: x.costPerGram,

              isIngredientCostUpdated: x.isIngredientCostUpdated,
              isIngredientCostDeleted: x.isIngredientCostDeleted,

              ingredient: x.recipeIngredient.ingredient,
              measurement: x.recipeIngredient.measurement,
              quantity: x.recipeIngredient.quantity,
            };
          }
        );
        this.listRecipeCostsForSubrecipes(response.data.result.recipeId);
      })
      .catch((error) => {
        this.$snotify.error(getErrorMessage(error, this.translations));
      });
  }

  // Get Recipe costs for subrecipes
  listRecipeCostsForSubrecipes(recipeId : number) {
    RecipeCostRepository.listRecipeCostsForSubrecipes(recipeId)
      .then((response) => {
        if (response.data.result.length > 0) {
          this.subRecipeCosts = response.data.result.map((x) => {
            return {
              id: x.subrecipeId,
              recipeName: x.recipeName,
              servings: x.recipeCostServings,
              totalCost: x.totalCost,
              isAnyIngredientCostDeleted: x.isAnyIngredientCostDeleted,
              isAnyIngredientCostUpdated: x.isAnyIngredientCostUpdated,
              isRecipeCostUpdated: x.isRecipeCostUpdated,
            };
          });
        }
      })
      .catch((error) => {
        this.$snotify.error(getErrorMessage(error, this.translations));
      });
  }

  // Get ingredient cost for Vendor
  getIngredientCostsForVendor(vendorId: number) {
    return this.allIngredientCosts.filter((x) => x.vendorId == vendorId);
  }

  // Calculate cost for recipe ingredient cost
  calculateCostForRecipeIngredientCost(
    quantity: number,
    measurementWeightInGram: number,
    ingrCostPerGram: number
  ) {
    return quantity * measurementWeightInGram * ingrCostPerGram;
  }

  formatNumber(number: number) {
    return (Math.round(number * 100) / 100).toFixed(2);
  }

  // Empty measurement json
  getEmptyMeasurment() {
    return {
      name: "",
      weight: 0,
    };
  }

  //Empty recipe cost
  getEmptyRecipeCost() {
    return {
      id: 0,
      recipeId: 0,
      recipe: this.getEmptyRecipe(),
      totalCost: 0,
      createdOn: "",
      recipeIngredientCosts: new Array(),
      isAnyIngredientCostDeleted: false,
      isAnyIngredientCostUpdated: false,
    };
  }

  //Empty recipe ingredient cost
  getEmptyRecipeIngredientCost() {
    return {
      id: 0,
      recipeIngredientId: 0,
      ingredientCostId: 0,
      ingredientCostName: "",
      ingredientCostVendorId: 0,
      costPerGram: 0,
      ingredient: "",
      measurement: "",
      quantity: 0,
      isIngredientCostUpdated: false,
      isIngredientCostDeleted: false,
    };
  }

  // Empty recipe
  getEmptyRecipe() {
    return {
      id: 0,
      name: "",
      description: "",
      instructions: "",
      recipeCategory: { id: -1, name: "" },
      nutritionAnalysis: {
        addedSugarPerServing: 0,
        unadjustedAddedSugarPerServing: 0,
        sugarAlcohols: 0,
        alcoholSugarDv: 0,
        ironDv: 0,
        iron: 0,
        calciumDv: 0,
        calcium: 0,
        potassiumDv: 0,
        potassium: 0,
        vitaminDDv: 0,
        vitaminD: 0,
        vitaminC: 0,
        sodiumDv: 0,
        sodium: 0,
        cholesterol: 0,
        cholesterolDv: 0,
        protein: 0,
        sugarsDv: 0,
        sugars: 0,
        fibreDv: 0,
        fibre: 0,
        carbohydrate: 0,
        carbohydrateDv: 0,
        transFatDv: 0,
        transFat: 0,
        saturatedFatDv: 0,
        saturatedFat: 0,
        fatDv: 0,
        fat: 0,
        calories: 0,
        servingWeight: 0,
        erythritol: 0,
        isomalt: 0,
        mannitol: 0,
        xylitol: 0,
        lactitol: 0,
        sorbitol: 0,
        maltitol: 0,
        unspecified: 0,
        saturatedAndTransDv: 0,
        servingQuantityInUnitOfMeasurement: 1,
        servingUnitOfMeasurement: "g",
      },
      childRecipes: new Array(),
      servings: 1,
      servingCount: 1,
      servingWeight: 0,
      servingWeightMeasurement: "g",
      recipeServingType: 0,
      weightInGrams: 0,
      weightPerServing: 0,
      addedSugars: 0,
      exactAddedSugars: 0,
      adjustedAddedSugarsValue: 0,
    };
  }

  // Empty ingredient cost json
  getEmptyIngredientCost() {
    return {
      name: "",
      vendorId: this.vendors.length > 0 ? this.vendors[0].id : null,
      quantity: 0,
      chosenMeasurementName:
        this.massMeasurements.length > 0 ? this.massMeasurements[0].name : null,
      ingredientCostMeasurements: [...this.massMeasurements],
      cost: 0,
    };
  }

  // Total recipe cost
  get totalRecipeCost() {
    let total = 0;
    for (let i = 0; i < this.recipeIngredientCosts.length; i++) {
      total += this.calculateCostForRecipeIngredientCost(
        this.recipeIngredientCosts[i].quantity,
        this.recipeIngredientCosts[i].measurement.weightInGrams,
        this.recipeIngredientCosts[i].costPerGram
      );
    }
    for (let i = 0; i < this.subRecipeCosts.length; i++) {
      total += this.subRecipeCosts[i].totalCost;
    }
    return total;
  }

  // Total recipe cost  per yield
  get costPerYield() {
    if (this.recipeCost.recipe && this.recipeCost.recipe.servings) {
      return this.totalRecipeCost / this.recipeCost.recipe.servings;
    }
    return 0;
  }

  get recipeCostsForGrid() {
    return this.recipeCosts.map((x) => {
      let notificationComponent: any = "";
      if (x.isAnyIngredientCostUpdated) {
        notificationComponent = {
          component: "vImageWithTooltip",
          props: {
            imgSrc: require(`@/assets/Customer/assets/img/msg-update.png`),
            tooltipContent: this.translations.get("rec-cost-updated-msg"),
            tooltipPlacement: "bottom-start",
            imgStyle: "height: 25px; margin-left: 2px;",
          },
        };
      }
      else if (x.isAnyIngredientCostDeleted) {
        notificationComponent = {
          component: "vImageWithTooltip",
          props: {
            imgSrc: require(`@/assets/Customer/assets/img/msg-delete.png`),
            tooltipContent: this.translations.get("rec-cost-deleted-msg"),
            tooltipPlacement: "bottom-start",
            imgStyle: "height: 22px; margin-left: 4px;",
          },
        };
      }

      return {
        meta: {
          id: x.id,
          link: x.recipe.name,
        },
        e1: (Math.round(x.totalCost * 100) / 100).toFixed(2),
        e2: this.$moment(x.createdOn).format(dateTimeDisplayFormat),
        e3: notificationComponent,
      };
    });
  }

  get tableHeaders() {
    return [
      {
        name: this.translations.get("ingr-cost-table-quantity"),
        key: "Quantity",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("ingr-cost-table-measurement"),
        key: "Measurement",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("rec-cost-table-ingr"),
        key: "Ingredient",
        searchable: true,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("ingr-cost-table-vendor"),
        key: "Vendor",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("rec-cost-table-search-ingr"),
        key: "SearchIngredient",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("ingr-cost-table-cost"),
        key: "Cost",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("rec-cost-table-notif"),
        key: "Notification",
        searchable: false,
        searchString: "",
        sortable: true,
      },
    ];
  }

  get tableHeaders2() {
    return [
      {
        name: this.translations.get("rec-cost-table-name"),
        key: "RecipeName",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("rec-cost-table-total-cost"),
        key: "TotalRecipeCost",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("rec-cost-table-date"),
        key: "DateCreated",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("rec-cost-table-notif"),
        key: "Notification",
        searchable: false,
        searchString: "",
        sortable: false,
      },
    ];
  }

  get translations() {
    return this.localizationState.translations;
  }

  // Download Button
  get downloadRecipeCostUrl() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/RecipeCosts/ExportRecipeCost?recipeCostId=${this.recipeCost.id}`;
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: [
          "common",
          "ingredients",
          "ingredient cost",
          "recipe costing",
        ],
      })
      .then(() => {
        document.title =
          this.translations.get("ingr-cost-title") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else
          this.$snotify.error(this.translations.get("toastr_network_error"));
      });

    this.loadAllIngredientCosts();
    this.loadIngredientCostVendors();
    this.getRecipeCostDetails();

    MeasurementRepository.listMeasurementsForCategory(MeasurementCategory.Mass)
      .then((response) => {
        let measurements = response.data.result.map((x: any) => {
          return {
            id: x.id,
            name: x.name,
            weightInGrams: x.conversionCoeficient,
          };
        });
        this.massMeasurements = measurements;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else
          this.$snotify.error(this.translations.get("toastr_network_error"));
      });
  }
}
export default RecipeCostComponent;
